// DrawerLocationsMulti.tsx
import * as React from "react";
import {
    Drawer, Box, TextField, IconButton, Typography, CircularProgress, Divider,
    Button, Stack, Chip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import type { TreeViewBaseItem } from "@mui/x-tree-view/models";
import type { AppCategory, DbCategory } from "../models/Category";
import { buildCategoryTree } from "../functions/categoryHelper";

// --- Internal types/helpers ---
type TreeNode = { id: string; label: string; children?: TreeNode[]; _locId: number };
const cmp = (a?: string | null, b?: string | null) => String(a ?? "").localeCompare(String(b ?? ""));
const toNum = (v: string | number | null | undefined) => {
    const n = Number(v); return Number.isFinite(n) ? n : NaN;
};

// Build from NESTED data
function fromNested(input: DbCategory[] | undefined): TreeNode[] {
    if (!Array.isArray(input)) return [];
    const visit = (n: DbCategory): TreeNode => {
        const idNum = n.categoryId;
        const label = String(n.categoryName ?? "");
        const kids: TreeNode[] = Array.isArray(n.children) ? n.children.map(visit) : [];
        kids.sort((a: TreeNode, b: TreeNode) => cmp(a.label, b.label));
        return { id: String(idNum), label, children: kids.length ? kids : undefined, _locId: idNum };
    };
    const roots = input.map(visit);
    roots.sort((a, b) => cmp(a.label, b.label));
    return roots;
}

// flatten tree to id->node map (for chips / lookup)
function flatten(nodes?: TreeNode[], out = new Map<string, TreeNode>()) {
    if (!nodes) return out;
    for (const n of nodes) {
        out.set(n.id, n);
        flatten(n.children, out);
    }
    return out;
}

// filter tree by label, keeping parents of matches
function filterTree(nodes: TreeNode[], q: string): TreeNode[] {
    const query = q.trim().toLowerCase();
    if (!query) return nodes;
    const visit = (n: TreeNode): TreeNode | null => {
        const match = n.label.toLowerCase().includes(query);
        const kids = (n.children ?? []).map(visit).filter(Boolean) as TreeNode[];
        return match || kids.length ? { ...n, children: kids.length ? kids : undefined } : null;
    };
    return nodes.map(visit).filter(Boolean) as TreeNode[];
}

type Props = {
    open: boolean;
    onClose: () => void;
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    onApply: (ids: number[]) => void;
    categories: AppCategory[] | DbCategory[];
    title?: string;
    allowMultiSelect?: boolean;
};

export const DrawerCategoryMulti: React.FC<Props> = ({
    open, onClose, selectedIds, onChange, onApply, categories, title = "Categories", allowMultiSelect = false,
}) => {
    const [query, setQuery] = React.useState("");
    const [busy, setBusy] = React.useState(false);

    // Build a tree from either flat or nested data
    const tree: TreeNode[] = React.useMemo(() => {
        const looksNested = Array.isArray(categories) && !!(categories as any[])[0]?.children;
        return looksNested ? fromNested(categories as DbCategory[]) : buildCategoryTree(categories as AppCategory[]);
    }, [categories]);

    const allMap = React.useMemo(() => flatten(tree), [tree]);

    // RichTreeView wants string ids; keep a controlled string[] selection
    const selectedItems = React.useMemo(() => selectedIds.map(String), [selectedIds]);
    const setSelectedItems = (ids: string[] | string | null) => {
        const arr = Array.isArray(ids) ? ids : [ids];
        const nums = arr.map((s) => Number(s)).filter((n) => Number.isFinite(n)) as number[];
        onChange(nums);
    };

    // Filtered view
    const filtered = React.useMemo(() => filterTree(tree, query), [tree, query]);

    // Chip helpers
    const chips = React.useMemo(
        () =>
            selectedItems
                .map((id) => allMap.get(id))
                .filter(Boolean) as TreeNode[],
        [selectedItems, allMap]
    );

    const removeOne = (id: string) => {
        const next = selectedItems.filter((s) => s !== id).map(Number);
        onChange(next);
    };

    const clearAll = () => onChange([]);

    const handleApply = async () => {
        setBusy(true);
        try {
            onApply(selectedIds);
            onClose();
        } finally {
            setBusy(false);
        }
    };

    // Expand all roots when searching; otherwise none (or keep your previous preference)
    const defaultExpanded = query ? filtered.map((n) => n.id) : [];

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100vw", sm: 400 }, display: "flex", maxWidth: "100%" } }}>
            {/* Header */}
            <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>{title}</Typography>
                <IconButton onClick={onClose} aria-label="Close"><CloseIcon /></IconButton>
            </Box>
            <Divider />

            {/* Search box */}
            <Box sx={{ p: 2, pt: 1 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search locations…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <Typography variant="caption" sx={{ mt: 0.5, display: "block", color: "text.secondary" }}>
                    Tip: Use Ctrl/⌘ to multi-select.
                </Typography>
            </Box>

            {/* Selected chips */}
            <Box sx={{ px: 2, pb: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {chips.length ? chips.map((n) => (
                        <Chip key={n.id} label={n.label} onDelete={() => removeOne(n.id)} />
                    )) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>No categories selected</Typography>
                    )}
                </Stack>
                {chips.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                        <Button size="small" onClick={clearAll}>Clear</Button>
                    </Box>
                )}
            </Box>

            {/* Tree */}
            <Box sx={{ px: 1, pb: 2, overflow: "auto", flex: 1 }}>
                {!categories ? (
                    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
                        <CircularProgress size={18} /><Typography variant="body2">Loading categories…</Typography>
                    </Box>
                ) : (
                    <>
                        <RichTreeView
                            items={filtered}
                                multiSelect={allowMultiSelect}
                            selectedItems={selectedItems}
                            onSelectedItemsChange={(_e, ids) => setSelectedItems(ids)}
                            defaultExpandedItems={defaultExpanded}
                            aria-label="Locations tree"
                        />
                        {!filtered.length && (
                            <Typography variant="body2" sx={{ px: 2, py: 1, color: "text.secondary" }}>
                                No matches
                            </Typography>
                        )}
                    </>
                )}
            </Box>

            {/* Footer actions */}
            <Divider />
            <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography variant="body2">Selected: {selectedItems.length}</Typography>
                <Box>
                    <Button size="small" onClick={clearAll} disabled={!selectedItems.length}>Clear</Button>
                    <Button size="small" variant="contained" sx={{ ml: 1 }} onClick={handleApply} disabled={busy}>
                        Apply
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};
